/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useSelector } from "react-redux";
import { emailValueSelector, selectedNotifySelector, severityValueSelector, snmpEmailGroupsSelector, snmpValueSelector } from "../../../../store/api/admin/selectors";
import { SEVERITYLIST } from "../../../../globals/globals";
import { useCallback, useEffect, useMemo, useState } from "react";
import { IEventConfig } from "../../../../store/api/deviceConfig/types";
import { useStoreDispatch } from "../../../../store/hooks";
import { setEmailValue, setGroupList, setSeverityValue, setSNMPEmailGroups, setSNMPValue } from "../../../../store/api/admin/reducers";
import { useGetEventConfigByGroupMutation, useUpdateEventConfigMutation } from "../../../../store/api/admin/adminApiSlice";
import { isLoadingStatusSelector } from "../../../../store/helpers/helperSelectors";
import { getEventsConfigurationColumns } from "../../../../components/Shared/Table/Columns";
import { setIsLoadingStatus, setOpenToast } from "../../../../store/helpers/helperReducers";
import { errorMessage } from "../../../../utils/helpers";
import { IKeyValue } from "../../types";
import { INotificationGrpData } from "../../../../store/api/admin/types";
import { useGetGroupsQuery } from "../../../../store/api/devices/deviceApiSlice";
import { searchKeywordByScopeSelector } from "../../../../store/search/selector";

export const useEventsData = () => {

    const selectedNotificationGroup = useSelector(selectedNotifySelector);
    const [configData, setEventConfigData] = useState<IEventConfig[]>([]);
    const [categoryFilter, setCategoryFilter] = useState<string>("");
    const searchKeyword = useSelector(searchKeywordByScopeSelector("eventConfiguration"));

    const categoryOptions = useMemo(() => {
        const categories = Array.from(new Set(configData.map((r) => r.category)
            .filter(Boolean))) as string[];
        return categories.sort();
    }, [configData]);

    const [isApplyToAll, setIsApplyToAll] = useState(false);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [selectedGroup] = useState<INotificationGrpData>({ text: "System Default", value: "NONE" });
    const [editSheetOpen, setEditSheetOpen] = useState(false);
    const [editingRow, setEditingRow] = useState<IEventConfig | null>(null);
    const [editFormValues, setEditFormValues] = useState({ severity: "", snmpManagerGroup: "", emailGroup: "" });
    const [filterValue, setFilterValue] = useState({
        severity: "",
        snmp: "",
        email: "",
    });

    const filteredConfigData = useMemo(() => {
        const q = searchKeyword.trim().toLowerCase();
        return configData.filter((row: IEventConfig) => {
            const matchesSearch = !q || [row.displayName, row.category, row.severity, row.snmpManagerGroup, row.emailGroup]
                .some((field) => field?.toLowerCase().includes(q));
            const matchesCategory = !categoryFilter || row.category === categoryFilter;
            const matchesSeverity = !filterValue.severity || row.severity === filterValue.severity;
            const matchesSnmp = !filterValue.snmp || filterValue.snmp === "None"
                ? !filterValue.snmp || !row.snmpManagerGroup
                : row.snmpManagerGroup === filterValue.snmp;
            const matchesEmail = !filterValue.email || filterValue.email === "None"
                ? !filterValue.email || !row.emailGroup
                : row.emailGroup === filterValue.email;
            return matchesSearch && matchesCategory && matchesSeverity && matchesSnmp && matchesEmail;
        });
    }, [configData, searchKeyword, categoryFilter, filterValue.severity, filterValue.snmp, filterValue.email]);

    const dispatch = useStoreDispatch();
    const snmpEmailGroups = useSelector(snmpEmailGroupsSelector);
    const [getEventConfigByGroup, configStatus] = useGetEventConfigByGroupMutation();
    const [updateEventConfig, statusEventConfig] = useUpdateEventConfigMutation();
    const isLoading = useSelector(isLoadingStatusSelector);
    const severityValue: IKeyValue = useSelector(severityValueSelector);
    const snmpValue = useSelector(snmpValueSelector);
    const emailValue = useSelector(emailValueSelector);
    const groups = useGetGroupsQuery();

    const selectSNMPProps = {
        id: "demo-select-small",
        labelId: "demo-select-small",
        inputLabel: "FORWARD TO SNMP",
        selectOptions: ["None", ...snmpEmailGroups?.snmpGroups],
    };

    const selectEmailProps = {
        id: "demo-select-xsmall",
        labelId: "demo-select-xsmall",
        inputLabel: "FORWARD TO EMAIL",
        formControlSx: { width: 150 },
        selectOptions: ["None", ...snmpEmailGroups?.emailGroups],
    };

    const severityProps = {
        id: "severity-select",
        labelId: "severity-select",
        inputLabel: "SEVERITY",
        selectOptions: SEVERITYLIST,
    };

    const fetchEventConfig = useCallback(
        async (value: string) => {
            const group = value === "System Default" ? "NONE" : value;
            try {
                const response: any = await getEventConfigByGroup({ logicalGroup: group });
                if (response?.error || configStatus?.isError) throw response?.error;
                if (!!response?.data && response?.data.length > 0) {
                    setEventConfigData(response?.data);
                }
            } catch (error: any) {
                dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
            }
        },
        [configStatus?.isError, dispatch, getEventConfigByGroup]
    );

    const applyToAllHandler = () => {
        const appliedToAllData = configData?.map((item: IEventConfig) => {
            let newDataApplyToAll = item;
            if (!!filterValue.severity) {
                dispatch(setSeverityValue({}));
                newDataApplyToAll = { ...newDataApplyToAll, severity: filterValue.severity };
            }

            if (!!filterValue.snmp) {
                dispatch(setSNMPValue({}));
                if (!!!item?.category || item?.category?.toLowerCase() === "custom" || filterValue.snmp === "None") {
                    newDataApplyToAll = { ...newDataApplyToAll, snmpManagerGroup: "" };
                } else {
                    newDataApplyToAll = { ...newDataApplyToAll, snmpManagerGroup: filterValue.snmp };
                }
            }

            if (!!filterValue.email) {
                dispatch(setEmailValue({}));
                newDataApplyToAll = { ...newDataApplyToAll, emailGroup: filterValue.email === "None" ? "" : filterValue.email };
            }

            return newDataApplyToAll;
        });

        setIsApplyToAll(true);
        setEventConfigData(appliedToAllData);
    };

    const handleOnSelect = (event: any) => {
        const { name, value } = event.target;
        setFilterValue({ ...filterValue, [name]: value });
    };

    const applyToSelectedHandler = async (
        fieldOrValues: "snmp" | "email" | { snmp?: string; email?: string },
        value?: string
    ) => {
        if (!selectedRows.length) return;

        const snmpValueToApply = typeof fieldOrValues === "object"
            ? fieldOrValues.snmp || ""
            : fieldOrValues === "snmp"
                ? value || ""
                : "";

        const emailValueToApply = typeof fieldOrValues === "object"
            ? fieldOrValues.email || ""
            : fieldOrValues === "email"
                ? value || ""
                : "";

        if (!snmpValueToApply && !emailValueToApply) return;

        const updated = configData.map((item: IEventConfig) => {
            if (!selectedRows.includes(item.key)) return item;

            let nextItem = { ...item };

            if (snmpValueToApply) {
                if (item?.category?.toLowerCase() === "custom" || item?.category?.trim() === "" || snmpValueToApply === "None") {
                    nextItem = { ...nextItem, snmpManagerGroup: "" };
                } else {
                    nextItem = { ...nextItem, snmpManagerGroup: snmpValueToApply };
                }
            }

            if (emailValueToApply) {
                nextItem = { ...nextItem, emailGroup: emailValueToApply === "None" ? "" : emailValueToApply };
            }

            return nextItem;
        });

        const isSaved = await submitHandler(undefined, updated);
        if (isSaved) {
            setEventConfigData(updated);
        }
        setIsApplyToAll(false);
    };

    const submitHandler = async (event?: any, overrideConfigData?: IEventConfig[]) => {
        if (event?.preventDefault) {
            event.preventDefault();
        }
        let result: any;
        let payload: IEventConfig[] = [];
        const sourceConfigData = overrideConfigData ?? configData;
    
        try {
            if (isApplyToAll) {
                sourceConfigData.length > 0 &&
                    sourceConfigData.forEach((item: IEventConfig) => {
                        payload.push({
                            key: item.key,
                            logicalGroup: item.logicalGroup,
                            severity: item.severity,
                            snmpManagerGroup: item.snmpManagerGroup,
                            emailGroup: item.emailGroup,
                            forwarded: item.forwarded,
                            groupName: item?.category || "",
                        });
                    });
            }
            else {
                sourceConfigData.length > 0 &&
                    sourceConfigData.forEach((item: IEventConfig) => {
                        let itemvalue = item;
                        if (Object.keys(severityValue).includes(item.key)) {
                            itemvalue = { ...itemvalue, severity: severityValue[item.key] };
                        }
                        if (Object.keys(snmpValue).includes(item.key)) {
                            itemvalue = { ...itemvalue, snmpManagerGroup: snmpValue[item.key] };
                        }
                        if (Object.keys(emailValue).includes(item.key)) {
                            itemvalue = { ...itemvalue, emailGroup: emailValue[item.key] };
                        }
                        payload.push({
                            key: itemvalue.key,
                            logicalGroup: itemvalue.logicalGroup,
                            severity: itemvalue.severity,
                            snmpManagerGroup: itemvalue.snmpManagerGroup,
                            emailGroup: itemvalue.emailGroup,
                            forwarded: itemvalue.forwarded,
                            groupName: item?.category || "",
                        });
                        // }
                    });
            }

            result = await updateEventConfig(payload);
            if (result?.error || statusEventConfig?.isError) throw result?.error;
            if (result.data?.status === 200 || result.data?.statusCode === 200) {
                dispatch(
                    setOpenToast({
                        openToast: true,
                        toastType: "success",
                        message: `The Event Configuration updates have been initiated.`,
                    })
                );
                setIsApplyToAll(false);
                setSelectedRows([]);
                setFilterValue({ severity: "", snmp: "", email: "" });
                dispatch(setSeverityValue({}));
                dispatch(setSNMPValue({}));
                dispatch(setEmailValue({}));
                fetchEventConfig(selectedGroup.text);
                return true;
            }
        } catch (error) {
            dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
        }
        return false;
    };

    const cancelHandler = () => {
        setFilterValue({ severity: "", snmp: "", email: "" });
        dispatch(setSeverityValue({}));
        dispatch(setSNMPValue({}));
        dispatch(setEmailValue({}));
        setEventConfigData(configData);
        fetchEventConfig(selectedGroup.text);
        setIsApplyToAll(false);
    };

    const handleEditOpen = useCallback((row: IEventConfig) => {
        setEditingRow(row);
        setEditFormValues({
            severity: row.severity || "",
            snmpManagerGroup: row.snmpManagerGroup || "",
            emailGroup: row.emailGroup || "",
        });
        setEditSheetOpen(true);
    }, []);

    const handleEditClose = useCallback(() => {
        setEditSheetOpen(false);
        setEditingRow(null);
    }, []);

    const handleEditSave = useCallback(async () => {
        if (!editingRow) return;
        const payload = [{
            key: editingRow.key,
            logicalGroup: editingRow.logicalGroup,
            severity: editFormValues.severity,
            snmpManagerGroup: editFormValues.snmpManagerGroup === "None" ? "" : editFormValues.snmpManagerGroup,
            emailGroup: editFormValues.emailGroup === "None" ? "" : editFormValues.emailGroup,
            forwarded: editingRow.forwarded,
            groupName: editingRow.category || "",
        }];
        try {
            const result: any = await updateEventConfig(payload);
            if (result?.error) throw result.error;
            if (result.data?.status === 200 || result.data?.statusCode === 200) {
                dispatch(setOpenToast({ openToast: true, toastType: "success", message: "Event configuration updated." }));
                setEditSheetOpen(false);
                setEditingRow(null);
                fetchEventConfig(selectedGroup.text || "NONE");
            }
        } catch (error) {
            dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
        }
    }, [editingRow, editFormValues, updateEventConfig, dispatch, fetchEventConfig, selectedGroup.text]);

    useEffect(() => {
        selectedGroup.text = selectedNotificationGroup.text;
        fetchEventConfig(selectedGroup.text || "NONE");
    }, [fetchEventConfig, selectedGroup.text, selectedNotificationGroup]);

    useEffect(() => {
        snmpEmailGroups?.data && dispatch(setSNMPEmailGroups(snmpEmailGroups?.data));
    }, [dispatch, snmpEmailGroups?.data]);

    useEffect(() => {
        dispatch(setIsLoadingStatus({ statusEventConfig: statusEventConfig.isLoading }));
    }, [dispatch, statusEventConfig.isLoading]);

    useEffect(() => {
        fetchEventConfig(selectedGroup.text || "NONE");
    }, [fetchEventConfig, selectedGroup.text]);

    useEffect(() => {
        const groupsData = groups.data || [];
        dispatch(setGroupList(groupsData));
    }, [groups.data, dispatch]);

    const eventsConfigurationColumns = useMemo(
        () => getEventsConfigurationColumns(
            (row) => handleEditOpen(row),
            (_row) => { /* TODO: wire Delete handler */ }
        ),
        [handleEditOpen]
    );

    return {
        filterValue,
        configData: filteredConfigData,
        configStatus,
        isLoading,
        eventsConfigurationColumns,
        selectSNMPProps,
        selectEmailProps,
        severityProps,
        handleOnSelect,
        applyToAllHandler,
        applyToSelectedHandler,
        submitHandler,
        cancelHandler,
        categoryFilter,
        setCategoryFilter,
        categoryOptions,
        selectedRows,
        setSelectedRows,
        editSheetOpen,
        editingRow,
        editFormValues,
        setEditFormValues,
        handleEditClose,
        handleEditSave,
    };
};